import { useKeycloak } from '@react-keycloak/web';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AuditResponse, PageResponseServiceTaskResponse } from '@/api/data-contracts';
import useTasks from '@/features/service-tasks/api/tasks.service';
import { queryKeys } from '@/react-query/constants';

type MonitorFormKey =
  | 'beforeDecorationFiles'
  | 'afterDecorationFiles'
  | 'beforeNeutralizationFiles'
  | 'afterNeutralizationFiles';

const FORM_KEY_TO_CATEGORY: Record<MonitorFormKey, string> = {
  beforeDecorationFiles: 'BEFORE_DECORATION',
  afterDecorationFiles: 'AFTER_DECORATION',
  beforeNeutralizationFiles: 'BEFORE_NEUTRALIZATION',
  afterNeutralizationFiles: 'AFTER_NEUTRALIZATION',
};

interface UseTaskImageUploadProps {
  taskId: string;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  queryKeyOverride?: (string | number)[];
}

export const useTaskImageUpload = ({
  taskId,
  onSuccess,
  onError,
  queryKeyOverride,
}: UseTaskImageUploadProps) => {
  const queryClient = useQueryClient();
  const { keycloak } = useKeycloak();
  const { uploadCampaignMonitorImages } = useTasks();

  return useMutation(
    ({ files, formKey }: { files: File[]; formKey: MonitorFormKey }) =>
      uploadCampaignMonitorImages(taskId, files, formKey),
    {
      onMutate: ({ formKey }) => {
        // Capture current objectIds for this formKey so we can detect newly added files in onSuccess
        const cacheKey = queryKeyOverride || [queryKeys.serviceOrders];
        const pages = queryClient.getQueriesData<PageResponseServiceTaskResponse>({
          queryKey: cacheKey,
        });
        const currentTask = pages
          .flatMap(([, data]) => data?.content ?? [])
          .find((r) => r.id === taskId);
        const previousObjectIds = new Set<string>(
          ((currentTask as any)?.[formKey] ?? [])
            .map((f: any) => f?.file?.objectId ?? f?.objectId)
            .filter(Boolean),
        );

        const optimisticId = `optimistic-${Date.now()}`;
        // Always inject the optimistic entry regardless of whether history was already loaded.
        // If it wasn't loaded yet (undefined), we set it to [entry] so it's ready when the widget mounts.
        const cachedHistory = queryClient.getQueryData<AuditResponse[]>([
          'services',
          taskId,
          'history',
        ]);
        queryClient.setQueryData<AuditResponse[]>(
          ['services', taskId, 'history'],
          [
            {
              id: optimisticId,
              timestamp: new Date().toISOString(),
              type: 'CAMPAIGN_MONITOR_PHOTOS_UPDATED',
              userId: (keycloak.tokenParsed as any)?.sub ?? '',
              userName: (keycloak.tokenParsed as any)?.name ?? '',
              organizationId: '',
              details: JSON.stringify({ serviceTaskId: taskId }),
              extraDetails: '[]',
              relatedObjectId: taskId,
            },
            ...(cachedHistory ?? []),
          ],
        );

        return { optimisticId, previousObjectIds };
      },
      onSuccess: (response, { files, formKey }, context) => {
        // Update cache directly with new files from response
        const cacheKey = queryKeyOverride || [queryKeys.serviceOrders];
        queryClient.setQueriesData<PageResponseServiceTaskResponse>(
          { queryKey: cacheKey },
          (old) => {
            if (!old?.content) return old;
            return {
              ...old,
              content: old.content.map((r) =>
                r.id === taskId
                  ? {
                      ...r,
                      beforeDecorationFiles: response.beforeDecorationFiles,
                      afterDecorationFiles: response.afterDecorationFiles,
                      beforeNeutralizationFiles: response.beforeNeutralizationFiles,
                      afterNeutralizationFiles: response.afterNeutralizationFiles,
                    }
                  : r,
              ),
            };
          },
        );

        if (context?.optimisticId) {
          const newFiles: Array<{ objectId: string; name: string }> = (
            (response as any)[formKey] ?? []
          )
            .map((f: any) => ({
              objectId: f?.file?.objectId ?? f?.objectId,
              name: f?.file?.name ?? f?.name ?? '',
            }))
            .filter((f: any) => f.objectId && !context.previousObjectIds.has(f.objectId));

          if (newFiles.length > 0) {
            const uploadedPhotos = newFiles.map((f) => ({
              category: FORM_KEY_TO_CATEGORY[formKey],
              objectId: f.objectId,
            }));

            // Update extraDetails so PhotoThumbnail mounts immediately with the real objectId
            queryClient.setQueryData<AuditResponse[]>(['services', taskId, 'history'], (prev) => {
              if (!prev) return prev;
              return prev.map((e) =>
                e.id === context.optimisticId
                  ? { ...e, extraDetails: JSON.stringify({ uploadedPhotos }) }
                  : e,
              );
            });

            // Pre-cache image blobs so useResourceImage returns them instantly without a
            // separate network round-trip. Match by filename first, fall back to index.
            const imgKeyPrefix = [queryKeys.documentBlob, 'service-task-campaign-monitor', taskId];
            newFiles.forEach(({ objectId, name }, index) => {
              const blob = files.find((f) => f.name === name) ?? files[index];
              if (blob) {
                queryClient.setQueryData([...imgKeyPrefix, String(objectId)], blob);
              }
            });
          }
        }

        // Invalidate after delay so the real server entry (outbox relay) replaces the optimistic one.
        // Use progressive retries since the outbox relay may take >1500ms.
        const historyKey = { queryKey: ['services', taskId, 'history'] };
        setTimeout(() => queryClient.invalidateQueries(historyKey), 2000);
        setTimeout(() => queryClient.invalidateQueries(historyKey), 4000);

        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (error) => {
        if (onError) {
          onError(error);
        }
      },
    },
  );
};
